/* eslint-disable eqeqeq */
import React, { useContext, useState, useEffect, useMemo } from 'react';
import { DropDownList } from 'components/formInputs/dropDownList';
import { Context as AuthContext } from 'contexts/auth';
import { Context as CompanyContext } from 'contexts/company';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Config } from 'configs';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { PageTitle } from 'components/layouts/pageTitle';
import { TextboxWrapper } from 'components/formInputs/wrappers/textboxWrapper';
import { ImageUpload } from 'components/formInputs/imageUpload';
import { files } from 'codemash';
import { FormButton } from 'components/buttons/formButton';
import { formFileUrlFromFileArray } from 'utils/imageHelper';
import { CheckboxWrapper } from 'components/formInputs/wrappers/checkboxWrapper';
import { TranslationFieldOptions, getValidationPairsForTranslatableText, getValidationShapeForTranslatableText } from 'utils/schemaHelper';
import { SelectedItemButton } from 'components/buttons/selectedItemButton';
import { TranslatableInput } from 'components/formInputs/tranlatableInput';
import { apiActionChooser } from 'utils/apiActionChooser';
import { postFile } from 'api/client/requests';

export const RestaurantEventsAddEditForm = ({ itemData, isEdit }) => {
  const navigate = useNavigate();

  const {
    state: companyState,
    addNew,
    editById,
    getCompanyRestaurants,
  } = useContext(CompanyContext);
  const { state: auth } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [coverImage, setCoverImage] = useState([]);
  const [selectedRestaurants, setSelectedRestaurants] = useState(itemData?.restaurants || []);

  useEffect(() => {
    getCompanyRestaurants({ token: auth.token, companyId: auth.company?.company });
  }, []);

  const schema = yup.object().shape({
    _id: yup.string().default(itemData?._id),
    name: yup
      .string()
      .required(
        'Field required',
      )
      .default(itemData?.first_name),
    order: yup
      .number()
      .nullable(true)
      .transform((_, val) => (val == Number(val) && val !== '' ? Number(val) : null)),
    restaurants: yup
      .array()
      .of(yup.string())
      .required('Please enter restaurants.')
      .min(1, 'Please select at least one restaurant.'),
    title: yup
      .object()
      .shape(
        getValidationShapeForTranslatableText(
          itemData?.name,
          TranslationFieldOptions.REQUIRE_AT_LEAST_ONE,
        ),
        getValidationPairsForTranslatableText(),
      ),
    external_link: yup.string().default(itemData?.external_link),
    hidden: yup.boolean().default(!!itemData?.hidden),
  });

  const {
    register,
    reset,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: useMemo(() => {
      return schema.default();
    }, [schema]),
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    setSelectedRestaurants(itemData?.restaurants || []);

    if (itemData?.image_cover) {
      setCoverImage([{ data_url: formFileUrlFromFileArray(itemData?.image_cover, true) }]);
    }

    reset(itemData);
  }, [itemData, reset]);

  const getRestaurantsDataSource = () => {
    const allRestaurants = [{ _id: '-1', name: '-' }, ...(companyState.restaurants || [])];

    return allRestaurants
      ?.filter((r) => !selectedRestaurants.includes(r._id))
      .map((restaurant) => ({
        label: restaurant.name,
        value: restaurant._id,
      }));
  };

  const deleteSelectedRestaurant = (restaurantId) => {
    const newRestaurants = selectedRestaurants.filter((r) => r !== restaurantId);

    setSelectedRestaurants(newRestaurants);
    setValue('restaurants', newRestaurants);
  };

  const onSubmit = async (data) => {
    setLoading(true);

    if (!isEdit) {
      await apiActionChooser(auth.company?.company, async () => {
        await addNew(
          {
            collectionName: Config.DATA.COLLECTIONS.RESTAURANT_EVENTS,
            data: {
              company: auth.company?.company,
              restaurants: data.restaurants,
              name: data.name,
              title: data.title,
              external_link: data.external_link,
              hidden: data.hidden,
              order: data.order || 0,
            },
            token: auth.token,
          },
          async (newId) => {
            if (coverImage && coverImage.length > 0) {
              await files.uploadRecordFile({
                collectionName: Config.DATA.COLLECTIONS.RESTAURANT_EVENTS,
                database: Config.CODEMASH.DATABASE_ID,
                recordId: newId,
                uniqueFieldName: 'image_cover',
                secretKey: auth.token,
                file: coverImage[0].file,
              });
            }

            navigate('/additional/events');
          },
          () => {
            setLoading(false);
          },
        );
      }, async () => {
        let comerImageId;

        if (coverImage && coverImage.length > 0) {
          const fileResp = await postFile({
            fieldPath: 'image_cover',
            file: coverImage[0].file,
            resource: 'RestaurantEvents',
          });

          comerImageId = fileResp.id;
        }

        await addNew(
          {
            data: {
              restaurants: data.restaurants,
              name: data.name,
              title: data.title,
              external_link: data.external_link,
              hidden: data.hidden,
              order: data.order || 0,
              image: comerImageId,
            },
            resourceV2: 'AdminRestaurantEvent',
            companyIdForV2: auth.company?.company,
          },
          async () => {
            navigate('/additional/events');
          },
          () => {
            setLoading(false);
          },
        );
      });
    } else {
      await apiActionChooser(auth.company?.company, async () => {
        await editById(
          {
            collectionName: Config.DATA.COLLECTIONS.RESTAURANT_EVENTS,
            id: data._id,
            updateClause: {
              $set: {
                restaurants: data.restaurants ? data.restaurants.map((x) => ({ $oid: x })) : [],
                name: data.name,
                title: data.title,
                external_link: data.external_link,
                hidden: data.hidden,
                order: data.order || 0,
              },
            },
            token: auth.token,
          },
          async () => {
            if (coverImage && coverImage.length > 0) {
              const uploadedNewImage =
                coverImage[0].file !== null && coverImage[0].file !== undefined;

              if (uploadedNewImage) {
                await files.uploadRecordFile({
                  collectionName: Config.DATA.COLLECTIONS.RESTAURANT_EVENTS,
                  database: Config.CODEMASH.DATABASE_ID,
                  recordId: data._id,
                  uniqueFieldName: 'image_cover',
                  secretKey: auth.token,
                  file: coverImage[0].file,
                });
              }
            } else {
              await editById({
                collectionName: Config.DATA.COLLECTIONS.RESTAURANT_EVENTS,
                database: Config.CODEMASH.DATABASE_ID,
                id: data._id,
                updateClause: { $set: { image_cover: null } },
                token: auth.token,
              });
            }

            navigate('/additional/events');
          },
          () => {
            setLoading(false);
          },
        );
      }, async () => {
        let deleteImage = false;
        let newImageId;

        if (coverImage && coverImage.length > 0) {
          if (coverImage[0].file !== null && coverImage[0].file !== undefined) {
            const fileResp = await postFile({
              fieldPath: 'image_cover',
              file: coverImage[0].file,
              resource: 'RestaurantEvents',
            });

            newImageId = fileResp.id;
          }
        } else {
          deleteImage = true;
        }

        await editById(
          {
            id: data._id,
            resourceV2: 'AdminRestaurantEvent',
            updateTypeV2: 'General',
            updateDataV2: {
              restaurants: data.restaurants,
              name: data.name,
              title: data.title,
              external_link: data.external_link,
              hidden: data.hidden,
              order: data.order || 0,
              image: newImageId,
              deleteImage,
            },
            companyIdForV2: auth.company?.company,
          },
          async () => {
            navigate('/additional/events');
          },
          () => {
            setLoading(false);
          },
        );
      });
    }
  };
  const onError = (data) => console.log(data);

  const renderSelectedRestaurants = () => {
    if (!selectedRestaurants) return null;

    return (
      <div>
        {selectedRestaurants.map((restaurant) => {
          const restaurantData = companyState.restaurants?.find((r) => r._id === restaurant);

          if (!restaurantData) return null;
          return (
            <div key={restaurant} className="mt-2">
              <SelectedItemButton
                key={restaurantData._id}
                name={restaurantData?.name}
                onClick={() => deleteSelectedRestaurant(restaurantData?._id)}
              />
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <PageTitle
        title="Event"
        breadcrumbs={[{ path: '/additional/events', title: 'Restaurant Events' }]}
      />
      <div className="container mx-auto px-2 md:px-6 lg:px-8">
        <div className="w-full">
          <form onSubmit={handleSubmit(onSubmit, onError)} action="#" method="POST">
            <div className="shadow sm:rounded-md sm:overflow-hidden">
              <div className="bg-white py-6 px-4 space-y-6 sm:p-6">
                <div>
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    {isEdit ? 'Edit event' : 'Add new event'}
                  </h3>
                </div>

                <div className="grid grid-cols-6 gap-6">
                  <CheckboxWrapper
                    register={register}
                    name="hidden"
                    title="Hidden"
                    responsiveClassName="col-span-6 sm:col-span-3"
                    disabled={itemData && itemData.unhide_disabled}
                    isDanger={itemData && itemData.unhide_disabled}
                  />
                  <div className="hidden sm:block sm:col-span-3" />

                  <div className="sm:col-span-3 col-span-6">
                    <DropDownList
                      showLabel
                      id="restaurants"
                      name="restaurants"
                      label="Restaurants*"
                      options={getRestaurantsDataSource()}
                      className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                      // disabled={isEdit}
                      onChange={(e) => {
                        setSelectedRestaurants((oldArray) => [...oldArray, e.target.value]);
                        setValue('restaurants', [...selectedRestaurants, e.target.value]);
                      }}
                    />
                    {errors?.restaurants && (
                      <p className="mt-2 text-sm text-red-600" id="email-error">
                        {errors?.restaurants.message}
                      </p>
                    )}
                    {renderSelectedRestaurants()}
                  </div>

                  <div className="sm:col-span-3 col-span-6">
                    <label htmlFor="image_cover" className="block text-sm font-medium text-gray-700 mb-1">
                      Cover photo
                    </label>
                    <ImageUpload id="image_cover" name="image_cover" images={coverImage} onChange={(val) => setCoverImage(val)} />
                  </div>

                  <TextboxWrapper
                    register={register}
                    name="name"
                    title="Name*"
                    responsiveClassName="col-span-6 sm:col-span-3"
                    error={errors?.name}
                  />

                  <div className="sm:col-span-3 col-span-6">
                    <TranslatableInput
                      name="title"
                      register={register}
                      error={errors?.title}
                      containerClassName="sm:col-span-3"
                      labelClassName="block text-sm font-medium text-gray-700"
                      className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      placeholder="Title*"
                    />
                  </div>

                  <TextboxWrapper
                    register={register}
                    name="external_link"
                    title="Link to event"
                    responsiveClassName="col-span-6 sm:col-span-3"
                    error={errors?.external_link}
                  />

                  <TextboxWrapper
                    register={register}
                    name="order"
                    title="Display order"
                    responsiveClassName="col-span-6 sm:col-span-3"
                    error={errors?.order}
                  />

                  <div className="col-span-4 sm:col-span-0 lg:col-span-4" />
                </div>
              </div>

              <div className="px-4 py-3 bg-gray-50 text-right sm:px-6">
                <button
                  type="button"
                  className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  onClick={() => navigate('/additional/events')}
                  disabled={loading}
                >
                  Cancel
                </button>
                <FormButton text="Save" isLoading={loading} />
              </div>

            </div>
          </form>
        </div>
      </div>
    </>
  );
};
